import { useState, useEffect } from "react";
import { useMusic } from "@/contexts/MusicContext";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  checkFanStreamLimit,
  consumeFanStream,
} from "@/utils/streamLimitChecker";
import { AppRole } from "@/types/supabase";
import { isUnlimitedStreamUser } from "@/utils/streamWhitelist";

interface UseFanStreamLimitProps {
  user: any;
  userRole: AppRole | null;
  trackId: string;
  streamDuration: number;
}

export function useFanStreamLimit({
  user,
  userRole,
  trackId,
  streamDuration,
}: UseFanStreamLimitProps) {
  const { setIsPlaying, setCurrentTrack } = useMusic();
  const queryClient = useQueryClient();
  const [fanLimitReached, setFanLimitReached] = useState(false);
  const [limitCheckProcessed, setLimitCheckProcessed] = useState(false);
  const [initialLimitCheck, setInitialLimitCheck] = useState(false);

  // Reset state when track changes
  useEffect(() => {
    console.log("🔄 Resetting fan limit state for track:", trackId);
    setFanLimitReached(false);
    setLimitCheckProcessed(false);
    setInitialLimitCheck(false);
  }, [trackId]);

  // Check fan daily limit immediately when track starts playing (for fans only)
  useEffect(() => {
    const checkInitialLimit = async () => {
      if (user && isUnlimitedStreamUser(user)) {
        console.log("⭐ Whitelisted user — bypassing fan daily stream limit");
        return true;
      }
      if (user && userRole === ("fan" as AppRole) && !initialLimitCheck) {
        console.log("👤 Checking initial fan daily stream limit");
        setInitialLimitCheck(true);

        const streamLimitCheck = await checkFanStreamLimit(user.id);
        console.log("📊 Initial fan stream limit check:", streamLimitCheck);

        // FAIL OPEN: If there's an error checking, allow playback
        if (streamLimitCheck.error) {
          console.warn(
            "⚠️ Could not verify stream limit, allowing playback:",
            streamLimitCheck.error,
          );
          return true;
        }

        // Only stop playback if we're CERTAIN there are no streams (no error, hasStreams = false)
        if (!streamLimitCheck.hasStreams) {
          console.log(
            "🚫 Fan has confirmed no remaining streams - stopping playback",
          );
          setFanLimitReached(true);
          setIsPlaying(false);

          toast.warning(
            "Daily stream limit reached! You've used all your streams for today.",
          );

          setTimeout(() => {
            setCurrentTrack(null);
          }, 1000);

          return false;
        }

        return true;
      }

      return null;
    };

    checkInitialLimit();
  }, [
    user,
    userRole,
    trackId,
    initialLimitCheck,
    setIsPlaying,
    setCurrentTrack,
  ]);

  // Check fan daily limit when minimum duration is reached and consume stream
  useEffect(() => {
    const checkFanLimit = async () => {
      if (user && isUnlimitedStreamUser(user)) {
        return null;
      }
      if (
        user &&
        userRole === ("fan" as AppRole) &&
        streamDuration >= 30 &&
        !limitCheckProcessed &&
        !fanLimitReached
      ) {
        console.log("👤 Checking fan daily stream limit for consumption");
        setLimitCheckProcessed(true);

        const streamLimitCheck = await checkFanStreamLimit(user.id);
        console.log(
          "📊 Fan stream limit check for consumption:",
          streamLimitCheck,
        );

        // FAIL OPEN: If there's an error, proceed with consumption attempt
        if (streamLimitCheck.error) {
          console.warn(
            "⚠️ Could not verify stream limit, proceeding with consumption:",
            streamLimitCheck.error,
          );
        } else if (!streamLimitCheck.hasStreams) {
          // Only stop if we're CERTAIN there are no streams
          console.log("🚫 Fan has confirmed no remaining streams");
          setFanLimitReached(true);
          setIsPlaying(false);

          toast.warning(
            "Daily stream limit reached! You've used all your streams for today.",
          );

          setTimeout(() => {
            setCurrentTrack(null);
          }, 1000);

          return false;
        }

        // Consume one stream from daily limit (has built-in retry and fail-open)
        const streamConsumed = await consumeFanStream(user.id);
        console.log("📉 Stream consumption result:", streamConsumed);

        // Don't stop playback on consumption failure - fraud prevention will handle abuse
        if (!streamConsumed) {
          console.warn(
            "⚠️ Stream consumption returned false, but allowing playback to continue",
          );
        } else {
          console.log("✅ Stream consumed from daily limit successfully");
        }

        // Invalidate daily stream limit query to update UI
        queryClient.invalidateQueries({
          queryKey: ["dailyStreamLimit", user.id],
        });
        return true;
      }

      return null;
    };

    checkFanLimit();
  }, [
    user,
    userRole,
    streamDuration,
    limitCheckProcessed,
    fanLimitReached,
    queryClient,
    setIsPlaying,
    setCurrentTrack,
    trackId,
  ]);

  return {
    fanLimitReached,
    limitCheckProcessed,
  };
}
